import { Chip, Typography } from "@platform/design-system";
import { Box, Stack } from "@mui/material";

import type { AttachmentSummary } from "../../messageSummary";

export interface AttachmentRowProps {
  attachment: AttachmentSummary;
}

/**
 * One file that came with a message.
 *
 * The design puts Preview and Download beside the name. Neither is offered
 * here, because neither can work: serving a file needs stored bytes and a
 * clean virus scan, and `downloadReady` is false on every attachment the
 * service returns today. A button that always refuses is worse than no
 * button, so the row names what arrived and says why it cannot be opened.
 */
export const AttachmentRow = ({
  attachment,
}: AttachmentRowProps): React.JSX.Element => (
  <Stack
    data-testid="attachment-row"
    data-attachment-id={attachment.id}
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      px: 1.5,
      py: 1,
    }}
  >
    <Chip
      component="span"
      size="small"
      color="default"
      variant="subtle"
      leadingVisual={false}
      label={attachment.kind}
    />

    <Typography
      data-testid="attachment-row-name"
      variant="body-medium"
      sx={{ flexGrow: 1, minWidth: 0, wordBreak: "break-all" }}
    >
      {attachment.fileName}
    </Typography>

    {attachment.size && (
      <Typography
        data-testid="attachment-row-size"
        variant="caption"
        color="textSecondary"
      >
        {attachment.size}
      </Typography>
    )}

    {attachment.unavailableReason && (
      <Box sx={{ flexShrink: 0 }}>
        <Typography
          data-testid="attachment-row-reason"
          variant="caption"
          color="textSecondary"
        >
          {attachment.unavailableReason}
        </Typography>
      </Box>
    )}
  </Stack>
);
